import {
  SkyBadge,
  SkyDrawer,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkyText,
  SkyTextarea,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useState } from "react";
import {
  GA4_DIMENSIONS_MAX,
  GA4_METRICS_MAX,
  ga4FullRefreshRequests,
  ga4ReportHints,
  ga4ReportProblems,
  parseGa4PropertyIds,
  splitGa4NameList,
  type Ga4ReportField,
} from "@shared/connectors/ga4Report";
import { targetTableName, type TableNaming } from "@shared/warehouse/targetTableName";
import type { ReportDefinitionValue } from "../EntityTable";
import {
  ReportFilterEditor,
  filterOf,
  filterStateOf,
  type ReportFilterState,
} from "./ReportFilterEditor";
import { fullRefreshDays, rebuildStartWords } from "./reportDialogs";

/**
 * #2428: Add report, Edit report and View report, for a connector whose entities are GA4 reports
 * (`entitySelection.definitionKind`). It carries a definition built and checked by
 * `shared/connectors/ga4Report.ts`, the rule the server runs again on Save, so a sentence here is
 * the sentence the 400 carries.
 *
 * - The property is a select from the connection's own property list as it stands in the form
 *   (`optionsFromFieldId`), so it works before the first save; one property is preselected.
 * - Dimensions and metrics are pasted or typed, split on commas or new lines, with a live preview,
 *   counters and the names that repeat or sit in both lists flagged (R18).
 * - The name is fixed once saved (it is the table name): Edit shows it read only.
 * - View (a reader below `dataflows.write`) shows every field read only, the property and the
 *   filter Hidden, as the server withholds them (#1107).
 *
 * `suggestionsFor(propertyId)` is a seam for dimension and metric names from the property's GA4
 * metadata, which needs a connector hub endpoint (DESIGN.md R9). Nothing passes it yet.
 */

export type ReportDrawerMode = "add" | "edit" | "view";

export interface ReportDrawerSubmit {
  name: string;
  definition: ReportDefinitionValue;
}

export interface ReportDrawerProps {
  open: boolean;
  mode: ReportDrawerMode;
  /** The report Edit, View or Duplicate starts from; null starts empty. */
  initial: { name: string; definition: ReportDefinitionValue } | null;
  /** The connection's property list as the form holds it now. */
  propertyIdsText: string;
  /** The connection's other reports' names, for the one-table-per-report rule. */
  otherNames: readonly string[];
  /** Names a removed report's landed table still holds; adding one rebuilds that table. */
  heldNames?: readonly string[];
  /** How the connection names its tables, for the live table name. */
  naming: TableNaming;
  /** The connection's history window, for the cost line. */
  historyDays: number;
  /**
   * The connection's History start date as the form holds it; when set it replaces the window for
   * the cost line and the re-add warning, as it does for every import (review of #2442).
   */
  startDate?: string;
  onSubmit: (report: ReportDrawerSubmit) => void;
  onClose: () => void;
  suggestionsFor?: (propertyId: string) => { dimensions: string[]; metrics: string[] } | null;
}

/** Shown in place of a value a reader may not see. */
export const HIDDEN_VALUE = "Hidden";
export const HIDDEN_REASON = "Only contributors and admins see connection details.";
/**
 * The property list is the connection form's field above the Reports, behind the drawer (review of
 * #2442); the select reads it while it is off (ClaudeBeta review 4).
 */
export const PROPERTY_IDS_FIRST = "Enter a property ID in GA4 Property IDs above first.";
/** Add needs no Connect first: names are checked by Google Analytics on the first run. */
export const NOT_CHECKED_UNTIL_FIRST_RUN = "Not checked until the first run.";

const TITLES: Record<ReportDrawerMode, string> = {
  add: "Add report",
  edit: "Edit report",
  view: "View report",
};

export const REPORT_DRAWER_INTRO =
  "Choose a property, the dimensions to break the numbers down by and the metrics to measure. Each report lands in its own table.";

interface DraftState {
  name: string;
  propertyId: string;
  dimensions: string;
  metrics: string;
  filter: ReportFilterState;
}

function draftOf(initial: ReportDrawerProps["initial"], mode: ReportDrawerMode): DraftState {
  const definition = initial?.definition;
  return {
    // Duplicate starts from a report with its name left empty.
    name: mode === "add" ? "" : (initial?.name ?? ""),
    propertyId: definition?.propertyId ?? "",
    dimensions: (definition?.dimensions ?? []).join(", "),
    metrics: (definition?.metrics ?? []).join(", "),
    filter: filterStateOf(definition?.dimensionFilter ?? null),
  };
}

/** The first sentence a field earns. */
function firstOf(
  problems: ReturnType<typeof ga4ReportProblems>,
  field: Ga4ReportField,
): string | null {
  return problems.find((problem) => problem.field === field)?.sentence ?? null;
}

/** Names repeated in one list or present in both, for the preview's flags. */
function flaggedNames(dimensions: readonly string[], metrics: readonly string[]): Set<string> {
  const flagged = new Set<string>();
  const seen = new Set<string>();
  for (const name of [...dimensions, ...metrics]) {
    if (seen.has(name)) flagged.add(name);
    seen.add(name);
  }
  return flagged;
}

/** The field's sentence when it is shown (after Add, or once the field is left), else undefined. */
type FieldError = (field: Ga4ReportField) => string | undefined;

function useReportDraft(props: ReportDrawerProps) {
  const { open, mode, initial, propertyIdsText, otherNames } = props;
  const readOnly = mode === "view";
  const [draft, setDraft] = useState<DraftState>(() => draftOf(initial, mode));
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [openedFor, setOpenedFor] = useState<string | null>(null);

  // Reopened for another report, or another mode: start from it (derive state from props).
  const openKey = open ? `${mode}:${initial?.name ?? ""}` : null;
  if (openKey !== openedFor) {
    setOpenedFor(openKey);
    if (openKey !== null) {
      setDraft(draftOf(initial, mode));
      setSubmitted(false);
      setTouched(new Set());
    }
  }

  const propertyOptions = useMemo(() => parseGa4PropertyIds(propertyIdsText), [propertyIdsText]);
  const onlyProperty = propertyOptions.length === 1 ? (propertyOptions[0] ?? "") : "";
  const propertyId = draft.propertyId || onlyProperty;
  const dimensions = useMemo(() => splitGa4NameList(draft.dimensions), [draft.dimensions]);
  const metrics = useMemo(() => splitGa4NameList(draft.metrics), [draft.metrics]);
  const filter = filterOf(draft.filter);
  const name = draft.name.trim();

  const definition: ReportDefinitionValue = {
    propertyId,
    dimensions,
    metrics,
    ...(filter.ok && filter.filter ? { dimensionFilter: filter.filter } : {}),
  };
  const problems = readOnly
    ? []
    : ga4ReportProblems(name, definition, {
        propertyIds: propertyOptions,
        otherNames: otherNames.filter((other) => other !== initial?.name || mode === "add"),
      });
  const touch = (field: string) =>
    setTouched((previous) => (previous.has(field) ? previous : new Set([...previous, field])));
  const errorOf: FieldError = (field) =>
    submitted || touched.has(field) ? (firstOf(problems, field) ?? undefined) : undefined;
  const showFilterError =
    submitted || touched.has("dimensionFilter") || draft.filter.mode === "json";
  const filterSentence = filter.ok ? firstOf(problems, "dimensionFilter") : filter.sentence;

  return {
    readOnly,
    draft,
    setDraft,
    touch,
    errorOf,
    filterError: showFilterError ? filterSentence : null,
    propertyOptions,
    propertyId,
    dimensions,
    metrics,
    name,
    definition,
    problems,
    /** Add or Save: shows every sentence, and hands the report on only when it has none. */
    submit: (): boolean => {
      setSubmitted(true);
      return problems.length === 0 && filter.ok;
    },
  };
}

function footerFor(
  mode: ReportDrawerMode,
  onClose: () => void,
  onSubmit: () => void,
): Array<{ label: string; onClick: () => void; appearance: "primary" | "secondary" }> {
  if (mode === "view") return [{ label: "Close", onClick: onClose, appearance: "secondary" }];
  return [
    { label: "Cancel", onClick: onClose, appearance: "secondary" },
    {
      label: mode === "edit" ? "Save report" : "Add report",
      onClick: onSubmit,
      appearance: "primary",
    },
  ];
}

export function ReportDrawer(props: ReportDrawerProps) {
  const { open, mode, initial, heldNames = [], naming } = props;
  const { historyDays, startDate = "", onSubmit, onClose } = props;
  const report = useReportDraft(props);
  const { readOnly, draft, setDraft, touch, errorOf, dimensions, metrics, name } = report;

  const flagged = flaggedNames(dimensions, metrics);
  const hints = ga4ReportHints(dimensions, metrics);
  const withheld = new Set(initial?.definition.withheld ?? []);
  const tableName =
    name && !firstOf(report.problems, "name")
      ? targetTableName({ ...naming, entityName: name })
      : null;
  const reAdd = mode === "add" && name !== "" && heldNames.includes(name);

  const handleSubmit = () => {
    if (!report.submit()) return;
    onSubmit({ name, definition: report.definition });
    onClose();
  };

  return (
    <SkyDrawer
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      position="end"
      size="medium"
      testId="report-drawer"
      headerTitle={TITLES[mode]}
      showCloseButton
      footerButtons={footerFor(mode, onClose, handleSubmit)}
    >
      <div className="flex flex-col gap-4">
        <SkyText as="p" size={200} color="muted" block>
          {REPORT_DRAWER_INTRO}
        </SkyText>

        <ReportNameField
          mode={mode}
          value={draft.name}
          error={errorOf("name")}
          tableName={tableName}
          reAddWarning={
            reAdd
              ? `A table already holds this name. Adding the report replaces that table, read again ${rebuildStartWords(startDate, historyDays)}.`
              : null
          }
          onBlur={() => touch("name")}
          onChange={(value) => setDraft({ ...draft, name: value })}
        />

        {withheld.has("propertyId") ? (
          <HiddenField label="Property" testId="report-property-hidden" />
        ) : (
          <PropertyField
            options={report.propertyOptions}
            value={report.propertyId}
            readOnly={readOnly}
            error={errorOf("propertyId")}
            onSelect={(value) => {
              touch("propertyId");
              setDraft({ ...draft, propertyId: value });
            }}
          />
        )}

        <NameListField
          field="dimensions"
          label="Dimensions"
          hint="The rows you want to break the numbers down by. Up to 9 GA4 API names, separated by commas or new lines, for example date, sessionSourceMedium."
          max={GA4_DIMENSIONS_MAX}
          noun="dimensions"
          text={draft.dimensions}
          names={dimensions}
          flagged={flagged}
          readOnly={readOnly}
          error={errorOf("dimensions") ?? null}
          onBlur={() => touch("dimensions")}
          onChange={(text) => setDraft({ ...draft, dimensions: text })}
        />
        <NameListField
          field="metrics"
          label="Metrics"
          hint="The numbers you want to measure. Up to 10 GA4 API names, separated by commas or new lines, for example sessions, totalUsers."
          max={GA4_METRICS_MAX}
          noun="metrics"
          text={draft.metrics}
          names={metrics}
          flagged={flagged}
          readOnly={readOnly}
          error={errorOf("metrics") ?? null}
          onBlur={() => touch("metrics")}
          onChange={(text) => setDraft({ ...draft, metrics: text })}
        />
        <ReportHints hints={hints} />

        {withheld.has("dimensionFilter") ? (
          <HiddenField label="Filter" testId="report-filter-hidden" />
        ) : (
          <ReportFilterEditor
            state={draft.filter}
            dimensions={dimensions}
            readOnly={readOnly}
            error={report.filterError}
            onChange={(next) => {
              touch("dimensionFilter");
              setDraft({ ...draft, filter: next });
            }}
          />
        )}

        {readOnly ? null : <ReportNotes days={fullRefreshDays(startDate, historyDays)} />}
      </div>
    </SkyDrawer>
  );
}

function ReportNameField(props: {
  mode: ReportDrawerMode;
  value: string;
  error: string | undefined;
  tableName: string | null;
  reAddWarning: string | null;
  onBlur: () => void;
  onChange: (value: string) => void;
}) {
  const { mode, error, tableName, reAddWarning } = props;
  return (
    <div className="flex flex-col gap-1">
      <SkyInput
        label="Report name"
        testId="report-name"
        required
        value={props.value}
        readOnly={mode !== "add"}
        placeholder="e.g. main_site"
        hint={
          mode === "edit"
            ? "To use another name, add a new report and remove this one."
            : "Used as the end of the table name. Letters, numbers and underscores, starting with a letter."
        }
        validationState={error ? "error" : "none"}
        validationMessage={error}
        onBlur={props.onBlur}
        onChange={(event) => props.onChange(event.target.value)}
      />
      {tableName ? (
        <SkyText size={200} color="muted" block testId="report-table-name">
          Lands as {tableName}
        </SkyText>
      ) : null}
      {reAddWarning ? (
        <SkyMessageBar intent="warning" testId="report-readd-warning" message={reAddWarning} />
      ) : null}
    </div>
  );
}

function PropertyField(props: {
  options: readonly string[];
  value: string;
  readOnly: boolean;
  error: string | undefined;
  onSelect: (value: string) => void;
}) {
  const { options, value, error } = props;
  const none = options.length === 0;
  return (
    <SkyDropdown
      label="Property"
      testId="report-property"
      required
      options={options.map((id) => ({ value: id, text: id }))}
      selectedOptions={value ? [value] : []}
      disabled={props.readOnly || none}
      placeholder={none ? PROPERTY_IDS_FIRST : "Choose a property"}
      validationState={error ? "error" : "none"}
      validationMessage={error}
      onOptionSelect={(values) => props.onSelect(values[0] ?? "")}
    />
  );
}

function ReportHints({ hints }: { hints: readonly string[] }) {
  if (hints.length === 0) return null;
  return (
    <div className="flex flex-col gap-1" data-testid="report-hints">
      {hints.map((hint) => (
        <SkyText key={hint} size={200} color="muted" block>
          {hint}
        </SkyText>
      ))}
    </div>
  );
}

function ReportNotes({ days }: { days: number }) {
  return (
    <>
      <SkyText size={200} color="muted" block testId="report-cost">
        About {ga4FullRefreshRequests(1, days, 1).toLocaleString()} requests for a Full refresh
        (reports times days, plus 2 for each property).
      </SkyText>
      <SkyText size={200} color="muted" block testId="report-not-checked">
        {NOT_CHECKED_UNTIL_FIRST_RUN} Google Analytics checks every name then, and a name it does
        not know fails this report only.
      </SkyText>
    </>
  );
}

function HiddenField({ label, testId }: { label: string; testId: string }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <SkyText size={300} weight="semibold" block>
        {label}
      </SkyText>
      <SkyText size={300} block>
        {HIDDEN_VALUE}
      </SkyText>
      <SkyText size={200} color="muted" block>
        {HIDDEN_REASON}
      </SkyText>
    </div>
  );
}

function NameListField(props: {
  field: "dimensions" | "metrics";
  label: string;
  hint: string;
  max: number;
  noun: string;
  text: string;
  names: readonly string[];
  flagged: ReadonlySet<string>;
  readOnly: boolean;
  error: string | null;
  onBlur: () => void;
  onChange: (text: string) => void;
}) {
  const { field, label, hint, max, noun, text, names, flagged, readOnly, error } = props;
  const over = names.length > max;
  return (
    <div className="flex flex-col gap-1">
      <SkyTextarea
        label={label}
        testId={`report-${field}`}
        required
        rows={3}
        resize="vertical"
        value={text}
        readOnly={readOnly}
        hint={hint}
        validationState={error ? "error" : "none"}
        validationMessage={error ?? undefined}
        onBlur={props.onBlur}
        onChange={(event) => props.onChange(event.target.value)}
      />
      <SkyText
        size={200}
        color={over ? "danger" : "muted"}
        block
        aria-live="polite"
        testId={`report-${field}-count`}
      >
        {names.length} of {max} {noun}
      </SkyText>
      {names.length > 0 ? (
        <div className="flex flex-wrap gap-1" aria-hidden="true">
          {names.map((name, index) => (
            <SkyBadge
              key={`${name}-${index}`}
              appearance="tint"
              color={flagged.has(name) ? "danger" : "subtle"}
              testId={`report-${field}-badge`}
            >
              {name}
            </SkyBadge>
          ))}
        </div>
      ) : null}
    </div>
  );
}
